import { AppointmentsStore } from './AppointmentsModel';
import { CompanyModel } from './CompanyModel';
import { CountryKey } from '../../../utils/wpo/codes';
import { DEFAULT_COMPANY_REGION } from '../../../config/config';
import { FETCH_OWN_ASSISTANCE_ITEMS, FETCH_RECOMMENDED_PATHS, GET_PROFILE, GET_RECKONING_RESULT } from '../../queries/User';
import { HAS_FACE_SCAN_RESULTS, SET_ACTIVE_PATH } from '../../mutations/User';
import { IRootStore } from '../Root/RootStore';
import { Instance, flow, getParent, getRoot, types } from 'mobx-state-tree';
import { StatModel } from './StatModel';
import { UserModel } from './UserModel';
import { captureException } from '../../../utils/sentry/sentry';
import { clientFunction } from '../../../config/apollo/client';
import { isNull } from 'lodash';

export const UserStore = types
  .model('UserStore', {
    user: types.maybeNull(UserModel),
    wpoAppointments: types.optional(AppointmentsStore, {}),
    stat: types.optional(StatModel, () => StatModel.create(
      {nbSessionsDone : null, number_of_path_started: null, number_of_path_completed: null})),
    company: types.maybeNull(CompanyModel),
    activationCode: types.maybeNull(types.string),
    recommendedPaths: types.maybeNull(types.array(types.string)),
    activePath: types.maybe(types.string),
    needCollectFeedbackAfterProgramChanged: types.maybeNull(types.boolean),
    readyForAFreshStartDateShown : types.maybeNull(types.Date),
    tabSelected: types.maybeNull(types.enumeration(['paths', 'activities'])),
    previousProgram: types.maybeNull(types.string),
    anonymous: types.maybeNull(types.boolean),
    GDPRAccepted: types.maybeNull(types.boolean),
    nbAISessions: types.maybeNull(types.number),
    nbAISessionsWithRequested: types.maybeNull(types.number),
    hasFaceScanResults: types.maybeNull(types.boolean),
    state: types.maybe(types.enumeration(['pending', 'done', 'error'])),
    stateGetProfile: types.maybe(types.enumeration(['pending', 'done', 'error'])),
    stateSetActivePath: types.maybe(types.enumeration(['pending', 'done', 'error'])),
  })
  .actions(self => {
    const fetchProfile = flow(function* (language: string) {
      self.stateGetProfile = 'pending';
      try {
        const { data }: any = yield clientFunction(language).query({ query: GET_PROFILE, fetchPolicy: 'no-cache' });
        if (data) {
          self.company = data.getMyProfile.company;
          if (self.user) {
            self.user.MHCNextDate = data.getMyProfile.MHCNextDate;
            self.user.matchingSurveyDone = data.getMyProfile.matchingSurveyDone;
            self.user.registeredWithWpo = data.getMyProfile.registeredWithWpo;
            if (data.getMyProfile.wpoRegion) {
              console.log('user.user.region: set region to fetched profile wpoRegion');
              self.user.region = data.getMyProfile.wpoRegion;
            }
            self.activePath = data.getMyProfile?.activePath?._id;
            getRoot<IRootStore>(self).onboarding.loadVisitedPages(data.getMyProfile.visitedPages);
            if(data.getMyProfile.nbSessionsDone) {
              self.stat.nbSessionsDone = data.getMyProfile.nbSessionsDone;
            }
            if (data.getMyProfile.recommendedPages) {
              self.user.recommendedPages = {
                recommendationStrategy: data.getMyProfile.recommendedPages.recommendationStrategy?._id || null,
                feature: data.getMyProfile.recommendedPages.feature?._id || null
              };
            }
          }
          if(self.user?.onboardingDone) {
            self.recommendedPaths = data.getMyProfile.recommendedPaths.slice().sort((p1: any, p2: any) => {
              p1.score > p2.score ? 1 : -1;
            }).map((r: any) => {
              return r.path._id;
            });
          }
        }
        self.stateGetProfile = 'done';
      } catch (error) {
        self.stateGetProfile = 'error';
        captureException(error);
        return null;
      }
    });

    const fetchReckoningResult = flow(function* (language?: string) {
      try {
        const { data }: any = yield clientFunction(language || 'en').query({ query: GET_RECKONING_RESULT, fetchPolicy: 'no-cache' });
        if (self?.user && data.getMyProfile.recommendedPages) {
          self.user.recommendedPages = {
            recommendationStrategy: data.getMyProfile.recommendedPages.recommendationStrategy?._id || null,
            feature: data.getMyProfile.recommendedPages.feature?._id || null
          };
        }

      } catch (error) {
        captureException(error);
        return null;
      }
    });
    const fetchOwnAssistanceItems = flow(function* (language: string) {
      self.state = 'pending';
      try {
        const { data }: any = yield clientFunction(language).query({ query: FETCH_OWN_ASSISTANCE_ITEMS, fetchPolicy: 'no-cache' });
        if (data && self.company) {
          self.company.ownAssistanceItems = data?.getMyProfile?.company?.ownAssistanceItems;
        }
        self.state = 'done';
      } catch (error) {
        self.state = 'error';
        captureException(error);
        return null;
      }
    });
    const setActivePath = flow(function* (pathId: string) {
      const formerActivePath = self.activePath;
      const formerStatus = getRoot<IRootStore>(self).path?.activePathStatus;
      try {
        self.stateSetActivePath = 'pending';
        self.activePath = pathId;
        getRoot<IRootStore>(self).path.changeActivePathStatus(pathId, 'ONGOING');
        const language = (getParent(self) as any).language.name.key;
        const { data }: any = yield clientFunction(language).mutate({ mutation: SET_ACTIVE_PATH, fetchPolicy: 'no-cache', variables: {pathId} });
        if (!data || !data.setActivePath) {
          throw Error('Could not set active path');
        } else {
          getRoot<IRootStore>(self)?.path.fetchPathWithTodaySession(pathId);
        }
        self.stateSetActivePath = 'done';
      } catch (error) {
        self.stateSetActivePath = 'error';
        self.activePath = formerActivePath;
        getRoot<IRootStore>(self).path.changeActivePathStatus(formerActivePath as string, formerStatus as string);
        captureException(error);
        self.stateSetActivePath = 'done';
        return null;
      }
    });

    const fetchRecommendedPath = flow(function* () {
      self.state = 'pending';
      const language = (getParent(self) as any).language.name.key;
      try {
        const { data }: any = yield clientFunction(language).query({ query: FETCH_RECOMMENDED_PATHS, fetchPolicy: 'no-cache' });
        if (data) {
          self.recommendedPaths = data.getMyProfile.recommendedPaths.slice().sort((p1: any, p2: any) => {
            p1.score > p2.score ? 1 : -1;
          }).map((r: any) => {
            return r.path._id;
          });
        }
        self.state = 'done';
      } catch (error) {
        self.state = 'error';
        captureException(error);
        return null;
      }
    });


    const clear= ()  => {
      self.user = null;
      self.anonymous = false;
      self.activationCode = null;
      self.recommendedPaths = null;
      self.company = null;
      self.GDPRAccepted  = false;
      self.state = 'done';
      self.stateGetProfile = 'done';
      self.stateSetActivePath = 'done';
      self.stat = StatModel.create({nbSessionsDone : 0, number_of_path_started: 0, number_of_path_completed: 0});
      self.nbAISessions = 0;
      self.nbAISessionsWithRequested = 0;
      self.activePath = undefined;
      self.tabSelected = 'paths';
      self.hasFaceScanResults = null;
    };

    const setHasFaceScanResults = (value: boolean) => {
      self.hasFaceScanResults = value;
    };

    const fetchFaceScanResults = flow(function* (language: string) {
      try {
        const { data }: any = yield clientFunction(language).mutate({ mutation: HAS_FACE_SCAN_RESULTS, fetchPolicy: 'no-cache' });
        self.hasFaceScanResults = data?.hasFaceScanResults === true;
      } catch (error) {
        captureException(error);
        self.hasFaceScanResults = false;
      }
    });

    const setNbAiSessions = (number: number) => {
      self.nbAISessions  = number;
    };

    const setTabSelected = (tab : 'paths' | 'activities')  => {
      self.tabSelected = tab;
    };

    const cancelTabSelected = ()  => {
      self.tabSelected = 'paths';
    };

    const setReadyForAFreshStartDateShown = () =>{
      self.readyForAFreshStartDateShown = new Date();
    };




    return {
      fetchProfile,
      fetchReckoningResult,
      fetchOwnAssistanceItems,
      fetchRecommendedPath,
      fetchFaceScanResults,
      setNbAiSessions,
      setActivePath,
      setTabSelected,
      cancelTabSelected,
      setReadyForAFreshStartDateShown,
      setHasFaceScanResults,
      register(userData: any) {
        self.user = userData.user;
        self.activationCode = userData.activationCode;
        self.anonymous = !userData.user?.email;
        getRoot<IRootStore>(self).onboarding.init();
        if(userData.user?.email) {
          self.GDPRAccepted = true;
        }
      },
      set(user: any) {
        self.user = user;
      },
      setPhoneNumber(phoneNumber: string) {
        if (self.user) {
          self.user.phoneNumber = phoneNumber;
        }
      },
      setRegion(region: CountryKey) {
        if (self.user) {
          self.user.region = region;
        }
      },
      setGDPRAccepted() {
        self.GDPRAccepted  = true;
      },
      incrementNbAiSession() {
        if(self.nbAISessions) {
          self.nbAISessions  = self.nbAISessions+1;
        } else {
          self.nbAISessions = 1;
        }
      },
      incrementNbAiSessionWithRequested() {
        if(self.nbAISessions) {
          self.nbAISessionsWithRequested = self.nbAISessions +1;
        } else {
          self.nbAISessionsWithRequested = 1;
        }
      },
      setGDPRNotAccepted() {
        self.GDPRAccepted  = false;
      },
      migrationForAnonimity() {
        if(self?.user && self.user?.email) {
          self.anonymous=false;
          self.GDPRAccepted = true;
        } else {
          if(isNull(self.GDPRAccepted)) {
            self.GDPRAccepted = false;
          }
        }
      },
      onboardingDone() {
        if (self.user) {
          self.user.onboardingDone = true;
        }
      },
      redoSessionDone() {
        if (self.user) {
          self.user.redoSessionDone = true;
        }
      },
      setNeedCollectFeedbackAfterProgramChanged(value: boolean, previousProgram?: string) {
        self.needCollectFeedbackAfterProgramChanged = value;
        if(value && !!previousProgram) {
          self.previousProgram = previousProgram;
        } else {
          self.previousProgram = null;
        }
      },
      MHCDone() {
        if (self.user && self.user.MHCNextDate) {
          self.user.MHCNextDate = String(new Date(Date.parse(self.user.MHCNextDate) + (1000 * 60 * 60 * 24 * 14)));
        }
      },
      matchingSurveyDone() {
        if(self.user) {
          self.user.matchingSurveyDone = true;
        }
      },
      clear
    };
  })
  .views(self => ({
    get languages() {
      if (!self.company) {
        console.warn('No company in user store. User langages defaults to "en"');
        return ['en']; // WPO defaults to English
      }
      const defaultCompanyRegion = {...DEFAULT_COMPANY_REGION };
      return (self.company.companyRegions || [defaultCompanyRegion])
        .filter(item => item.region === self.user?.region)
        .map(item => item.language);
    },
    get regions() {
      if(self?.company?.companyRegions && self?.company?.companyRegions.length>0) {
        const regions = self?.company?.companyRegions.map(item => item.region);
        return Array.from(new Set(regions));
      } else {
        return ['BE'];
      }
    },
    get isAdmin() {
      return self.user?.role === 'Admin';
    },
    get getUser() {
      return self.user;
    },
    get getCompany() {
      return self.company;
    },
    get phoneNumber() {
      return self.user?.phoneNumber;
    },
    get nbAiSessions() {
      return self.nbAISessions;
    },
    get freshStartShown() {
      if(!self?.readyForAFreshStartDateShown) { return true;}
      return Number(new Date()) - Number(self?.readyForAFreshStartDateShown) > 1000 * 60 * 60 * 24 ;
    },
    getRecommendedPaths() {
      if (self.recommendedPaths) {
        return self.recommendedPaths;
      } else {
        return null;
      }

    },

  }));

export type IUserStore = Instance<typeof UserStore>;
